import React, { FormEvent, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useSnackbar } from "notistack";
import { NOTISTACK_SUCCESS_OPTIONS } from "constants/index";
import {
  SECRET_VALUE_MAX_BYTES,
  Secret,
  useCreateSecretMutation,
  useLazyGetSecretsPublicKeyQuery,
  useUpdateSecretMutation,
} from "features/settings/secrets";
import {
  SecretTooLongError,
  SecureContextError,
  byteLength,
  encryptSecret,
} from "features/settings/encryptSecret";

export const MONOSPACE =
  'ui-monospace, "SFMono-Regular", "Menlo", "Consolas", monospace';

const NAME_MAX_LENGTH = 100;
const ENCRYPTION_UNAVAILABLE = "Encryption is unavailable on this stack";

interface FormErrors {
  name?: string;
  description?: string;
  value?: string;
  form?: string;
}

const firstMessage = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === "string") {
    return value[0];
  }
  return undefined;
};

const tooLongMessage = (bytes: number, max: number) =>
  `The value is ${bytes} bytes; the limit is ${max} bytes.`;

/**
 * DRF answers a rejected write with `{field: [messages]}`, or `detail` for a
 * refusal about no one field; a 503 means KMS cannot be used here. Anything
 * else gets a generic line rather than nothing.
 */
const toFormErrors = (error: unknown, fallback: string): FormErrors => {
  if (typeof error !== "object" || error === null) {
    return { form: fallback };
  }
  if ("status" in error && error.status === 503) {
    return { form: ENCRYPTION_UNAVAILABLE };
  }
  const data = "data" in error ? error.data : undefined;
  if (typeof data !== "object" || data === null) {
    return { form: fallback };
  }
  const fields = data as Record<string, unknown>;
  const errors: FormErrors = {
    name: firstMessage(fields.name),
    description: firstMessage(fields.description),
    value: firstMessage(fields.value),
    form:
      firstMessage(fields.requires_encryption) ??
      firstMessage(fields.non_field_errors) ??
      firstMessage(fields.detail),
  };
  if (!errors.name && !errors.description && !errors.value && !errors.form) {
    errors.form = fallback;
  }
  return errors;
};

interface SecretDialogProps {
  /** The secret to edit; null to create one. */
  secret: Secret | null;
  onClose: () => void;
}

/**
 * Creates or edits a secret. The plaintext lives only in this component's
 * state, which the parent unmounts on close; an encrypted value is encrypted
 * here and only its ciphertext is sent.
 */
const SecretDialog = ({ secret, onClose }: SecretDialogProps) => {
  const isEdit = secret !== null;
  const [name, setName] = useState("");
  const [description, setDescription] = useState(secret?.description ?? "");
  const [requiresEncryption, setRequiresEncryption] = useState(
    secret?.requires_encryption ?? true,
  );
  // An encrypted value is never sent to the browser, so its edit starts empty.
  const [value, setValue] = useState(secret?.value ?? "");
  const [showValue, setShowValue] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isEncrypting, setIsEncrypting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [getPublicKey] = useLazyGetSecretsPublicKeyQuery();
  const [createSecret, { isLoading: isCreating }] = useCreateSecretMutation();
  const [updateSecret, { isLoading: isUpdating }] = useUpdateSecretMutation();
  const isBusy = isEncrypting || isCreating || isUpdating;

  const bytes = byteLength(value);
  const isTooLong = bytes > SECRET_VALUE_MAX_BYTES;
  const keepsEncryptedValue = isEdit && requiresEncryption && value === "";

  const validate = (): FormErrors => {
    const found: FormErrors = {};
    if (!isEdit && name.trim() === "") {
      found.name = "Enter a name.";
    }
    if (value === "" && !keepsEncryptedValue) {
      found.value = "Enter a value.";
    } else if (isTooLong) {
      found.value = tooLongMessage(bytes, SECRET_VALUE_MAX_BYTES);
    }
    return found;
  };

  /** The value as it may be sent: ciphertext when the secret is encrypted. */
  const outgoingValue = async (): Promise<string> => {
    if (!requiresEncryption) {
      return value;
    }
    setIsEncrypting(true);
    try {
      const key = await getPublicKey(undefined, false).unwrap();
      return await encryptSecret(value, key);
    } finally {
      setIsEncrypting(false);
    }
  };

  const save = async (): Promise<string | null> => {
    if (!isEdit) {
      const created = await createSecret({
        name: name.trim(),
        description: description.trim(),
        requires_encryption: requiresEncryption,
        value: await outgoingValue(),
      }).unwrap();
      return `Secret "${created.name}" created.`;
    }
    const changes: { description?: string; value?: string } = {};
    if (description.trim() !== secret.description.trim()) {
      changes.description = description.trim();
    }
    if (!keepsEncryptedValue && value !== secret.value) {
      changes.value = await outgoingValue();
    }
    if (Object.keys(changes).length === 0) {
      return null;
    }
    await updateSecret({ id: secret.id, ...changes }).unwrap();
    return `Secret "${secret.name}" updated.`;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    try {
      const message = await save();
      if (message) {
        enqueueSnackbar(message, NOTISTACK_SUCCESS_OPTIONS);
      }
      onClose();
    } catch (error) {
      if (error instanceof SecretTooLongError) {
        setErrors({ value: tooLongMessage(error.bytes, error.max) });
      } else if (error instanceof SecureContextError) {
        setErrors({ form: error.message });
      } else {
        setErrors(
          toFormErrors(
            error,
            isEdit
              ? "Failed to update the secret."
              : "Failed to create the secret.",
          ),
        );
      }
    }
  };

  const valueHelper = keepsEncryptedValue
    ? "Leave empty to keep the current value."
    : requiresEncryption
      ? "Encrypted in your browser before it is sent; it can never be shown again."
      : "Not sensitive: shown to everyone who can open this tab.";

  return (
    <Dialog
      open
      onClose={isBusy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="secret-dialog-title"
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle id="secret-dialog-title">
          {isEdit ? "Edit secret" : "New secret"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" mb={2}>
            {isEdit
              ? "Only the value and the description can be changed."
              : "The name and whether the value is encrypted cannot be changed later."}
          </DialogContentText>
          {errors.form && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.form}
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2}>
            {isEdit ? (
              <>
                <TextField
                  size="small"
                  label="Name"
                  value={secret.name}
                  // Wraps, so a long name is never cut off.
                  multiline
                  InputProps={{ readOnly: true }}
                  inputProps={{ style: { fontFamily: MONOSPACE } }}
                />
                <TextField
                  size="small"
                  label="Requires encryption"
                  value={secret.requires_encryption ? "Yes" : "No"}
                  InputProps={{ readOnly: true }}
                />
              </>
            ) : (
              <>
                <TextField
                  autoFocus
                  required
                  size="small"
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  error={Boolean(errors.name)}
                  helperText={errors.name ?? "Up to 100 characters; unique."}
                  inputProps={{
                    maxLength: NAME_MAX_LENGTH,
                    spellCheck: false,
                    autoComplete: "off",
                    style: { fontFamily: MONOSPACE },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={requiresEncryption}
                      disabled={isBusy}
                      onChange={(e) => {
                        setRequiresEncryption(e.target.checked);
                        // A value typed as sensitive must not reappear as
                        // plain text when the switch is turned off.
                        setValue("");
                        setShowValue(false);
                      }}
                    />
                  }
                  label="Requires encryption"
                />
              </>
            )}
            <TextField
              size="small"
              label="Description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors((prev) => ({ ...prev, description: undefined }));
              }}
              error={Boolean(errors.description)}
              helperText={errors.description ?? "Optional."}
              multiline
              minRows={2}
            />
            <Box>
              <TextField
                fullWidth
                required={!keepsEncryptedValue}
                size="small"
                label={isEdit && requiresEncryption ? "New value" : "Value"}
                type={requiresEncryption && !showValue ? "password" : "text"}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setErrors((prev) => ({ ...prev, value: undefined }));
                }}
                error={Boolean(errors.value) || isTooLong}
                helperText={errors.value ?? valueHelper}
                // Kept out of the browser's and password managers' stores.
                inputProps={{
                  spellCheck: false,
                  autoComplete: "off",
                  "data-1p-ignore": "true",
                  "data-lpignore": "true",
                  style: { fontFamily: MONOSPACE },
                }}
                InputProps={
                  requiresEncryption
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label={
                                showValue ? "Hide value" : "Show value"
                              }
                              onClick={() => setShowValue((shown) => !shown)}
                            >
                              {showValue ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
              <Typography
                variant="caption"
                display="block"
                textAlign="right"
                color={isTooLong ? "error" : "text.secondary"}
                mt={0.5}
              >
                {bytes} / {SECRET_VALUE_MAX_BYTES} bytes
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isBusy}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SecretDialog;
